import React from 'react';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import clsx from 'clsx';
import TicketGroup from './TicketGroup';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatPrice } from '../services/formatPrice';
import Label from './CheckoutForm/components/Label';
import checkIsTicketGroupVisible from '../lib/checkIsTicketGroupVisible';
import { getImageSrcAndSrcSet } from '../services/getUrlFromCdn';
import checkIsTicketGroupScheduled from '../lib/checkIsTicketGroupScheduled';
import { getRuleHiddenTicketTypeIds } from '../lib/pruneRuleHiddenTickets';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import StyledElement from '@repo/checkout/components/StyledElement';

export default function TicketGroups() {
  const { checkout: page, checkoutLayout, isPreview, form, query, theme } = useCheckoutState();
  const aiTarget = useAiTarget();

  const { ticketGroups: formTicketGroups } = form;

  const eventDetails = page?.eventDetails || {};

  // only show enabled ticket groups
  // only show ticket groups with ticket types
  const ticketGroups = (page?.ticketGroups || [])
    .filter(ticketGroup => ticketGroup.status === 'enabled')
    .filter(ticketGroup => ticketGroup.ticketTypes.length > 0);

  if (!ticketGroups.length) {
    return null;
  }

  const isEventSoldOut = page.eventDetails?.isSoldOut;
  const isEventCanceled = page.eventDetails?.status === 'canceled';

  const ruleHiddenIds = getRuleHiddenTicketTypeIds(page.ticketGroups, formTicketGroups);
  const isHiddenByRules = ticketGroup => {
    const ticketTypes = ticketGroup.ticketTypes.filter(type => type.status === 'enabled');
    return ticketTypes.length > 0 && ticketTypes.every(type => ruleHiddenIds.has(String(type._id)));
  };

  if (ticketGroups.length === 1 && !ticketGroups[0].layout?.collapse) {
    const ticketGroup = ticketGroups[0];

    const ticketGroupScheduleResult = checkIsTicketGroupScheduled({
      ticketGroup,
      allTicketGroups: ticketGroups,
    });

    const visible =
      !isHiddenByRules(ticketGroup) &&
      checkIsTicketGroupVisible({
        page,
        ticketGroup,
        query,
        isSoldOut: ticketGroup.isSoldOut || isEventSoldOut,
        isOnSale: ticketGroup.isOnSale,
        isCanceled: isEventCanceled,
        hasInvalidReference: ticketGroupScheduleResult.hasInvalidReference,
        isScheduled: ticketGroupScheduleResult.isScheduled,
      });

    if (!visible && !isPreview) return null;

    return (
      <div className="cp_ticket-groups" {...aiTarget('ticketTypes')}>
        <div
          className={clsx(
            `cp_ticket-group-wrapper cp_ticket-group-${ticketGroup.reference}-wrapper`,
            'px-4',
            {
              'opacity-25 pointer-events-none': !visible && isPreview,
            },
          )}
        >
          <TicketGroup
            ticketGroup={ticketGroup}
            currency={eventDetails.currency}
            isTicketGroupVisible={visible}
          />
        </div>
      </div>
    );
  }

  const labels = getThemeLabels(theme);

  const visibleTicketGroups = ticketGroups
    .map(ticketGroup => {
      const ticketGroupScheduleResult = checkIsTicketGroupScheduled({
        ticketGroup,
        allTicketGroups: ticketGroups,
      });

      const visible =
        !isHiddenByRules(ticketGroup) &&
        checkIsTicketGroupVisible({
          page,
          ticketGroup,
          query,
          isSoldOut: ticketGroup.isSoldOut || isEventSoldOut,
          isOnSale: ticketGroup.isOnSale,
          isCanceled: isEventCanceled,
          hasInvalidReference: ticketGroupScheduleResult.hasInvalidReference,
          isScheduled: ticketGroupScheduleResult.isScheduled,
        });

      return { ticketGroup, visible, ticketGroupScheduleResult };
    })
    .filter(({ visible }) => visible || isPreview);

  return (
    <div
      {...aiTarget('ticketTypes')}
      className={clsx('cp_ticket-groups', {
        'mx-4':
          checkoutLayout === checkoutLayouts.SPLIT || checkoutLayout === checkoutLayouts.DOUBLE,
      })}
    >
      {visibleTicketGroups.map(({ ticketGroup, visible, ticketGroupScheduleResult }, index) => {
        const isFirst = index === 0;
        const isLast = index === visibleTicketGroups.length - 1;

        // not accordion
        if (!ticketGroup.layout?.collapse) {
          return (
            <React.Fragment key={ticketGroup._id}>
              <div
                className={clsx(
                  `cp_ticket-group-wrapper cp_ticket-group-${ticketGroup.reference}-wrapper`,
                  'first:pt-0 pt-4',
                  {
                    'opacity-25 pointer-events-none': !visible && isPreview,
                    'px-4': checkoutLayout === checkoutLayouts.CENTER,
                  },
                )}
              >
                <TicketGroup
                  ticketGroup={ticketGroup}
                  currency={eventDetails.currency}
                  isTicketGroupVisible={visible}
                  isTicketGroupScheduled={ticketGroupScheduleResult.isScheduled}
                  ticketGroupHasInvalidReference={ticketGroupScheduleResult.hasInvalidReference}
                />
              </div>
            </React.Fragment>
          );
        }

        const selectedTicketTypes = ticketGroup.ticketTypes
          .filter(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            if (!formTicketType) return false;

            return formTicketType.quantity > 0;
          })
          .map(ticketType => {
            const formTicketType = formTicketGroups?.[ticketGroup._id]?.find(
              formTicketGroup => formTicketGroup.ticketTypeId === ticketType._id,
            );

            if (!formTicketType) return false;

            return {
              ...ticketType,
              quantity: formTicketType.quantity,
            };
          });

        // accordion
        return (
          <React.Fragment key={ticketGroup._id}>
            {isFirst && (
              <StyledElement
                elementPath="checkout.ticketGroups.ticketTypes.divider"
                className="cp_ticket-group-divider border-b border-[var(--cp_checkout-divider-border-color)]"
              />
            )}
            <Disclosure
              as="div"
              className={clsx(
                'cp_ticket-type-accordion-wrapper',
                'last:-mb-4', // collapse margin on the last accordion wrapper
                {
                  'opacity-25 pointer-events-none': !visible && isPreview,
                  'px-4': checkoutLayout === checkoutLayouts.CENTER,
                },
              )}
            >
              {({ open }) => (
                <>
                  <DisclosureButton
                    className={clsx('cp_ticket-type-accordion', 'py-2 my-2 w-full')}
                  >
                    <div className="cp_ticket-type-accordion-label-wrapper flex justify-between items-center">
                      <Label
                        className="cp_ticket-type-accordion-label"
                        style={{ marginBottom: 0, cursor: 'inherit' }}
                        required={ticketGroup.required}
                      >
                        {ticketGroup.name}
                      </Label>
                      <ChevronDownIcon className="cp_ticket-type-accordion-label-icon h-5 w-5" />
                    </div>

                    {/* selected ticket types in closed accordion */}
                    {selectedTicketTypes &&
                      selectedTicketTypes.length > 0 &&
                      !open &&
                      selectedTicketTypes.map(selectedTicketType => (
                        <div
                          className={clsx(
                            'cp_ticket-type-accordion-selected-ticket-type',
                            'text-left mt-2 flex',
                          )}
                          key={selectedTicketType.ticketTypeId}
                        >
                          {ticketGroup.ticketSelectionType === 'quantity' && (
                            <div className="cp_ticket-type-accordion-selected-ticket-type-quantity-wrapper mr-1 whitespace-nowrap">
                              <span className="cp_ticket-type-accordion-selected-ticket-type-quantity">
                                {selectedTicketType.quantity}
                              </span>
                              <XMarkIcon className="cp_ticket-type-accordion-selected-ticket-type-quantity-icon w-3 h-3 mt-0.5 inline-block" />
                            </div>
                          )}

                          {ticketGroup.ticketSelectionType !== 'quantity' && (
                            <CheckIcon className="cp_ticket-type-accordion-selected-ticket-type-icon w-5 h-5 mr-1 mt-0.5" />
                          )}

                          <div className="cp_ticket-type-accordion-selected-ticket-type-label-wrapper">
                            <SelectedAccordionTicketTypeLabel
                              ticketType={selectedTicketType}
                              showName={ticketGroup.layout?.showTicketTypeName}
                              showPrice={ticketGroup.layout?.showTicketTypePrices}
                              alignment={ticketGroup.layout?.textAlign}
                              imageSize={ticketGroup.layout?.imageSize}
                              soldOutText={labels.soldOutText}
                            />
                          </div>
                        </div>
                      ))}
                  </DisclosureButton>

                  <DisclosurePanel
                    className={clsx(
                      `cp_ticket-group-wrapper cp_ticket-group-${ticketGroup.reference}-wrapper`,
                      'last:pb-4', // add padding to the last variant wrapper
                    )}
                  >
                    <TicketGroup
                      isAccordion
                      ticketGroup={ticketGroup}
                      currency={eventDetails.currency}
                      isTicketGroupVisible={visible}
                      isTicketGroupScheduled={ticketGroupScheduleResult.isScheduled}
                      ticketGroupHasInvalidReference={ticketGroupScheduleResult.hasInvalidReference}
                    />
                  </DisclosurePanel>
                </>
              )}
            </Disclosure>
            {!isLast && (
              <StyledElement
                elementPath="checkout.ticketGroups.ticketTypes.divider"
                className="cp_ticket-group-divider border-b border-[var(--cp_checkout-divider-border-color)]"
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SelectedAccordionTicketTypeLabel({ ticketType, showName, showPrice, alignment = 'left' }) {
  const { checkout: page, isPreview, currencyLocale } = useCheckoutState();

  const price = formatPrice({
    amount: ticketType.price,
    currency: page.eventDetails?.currency,
    locale: currencyLocale,
    style: 'currency',
  });

  const image = getImageSrcAndSrcSet({
    url: ticketType.image?.url,
    width: 48,
    height: 48,
    isPreview,
  });

  return (
    <div className="cp_ticket-type-label-wrapper flex flex-wrap w-full gap-2">
      <div
        className={clsx('cp_ticket-type-label-container flex flex-nowrap w-full gap-x-2', {
          'text-left': alignment === 'left',
        })}
      >
        {image && (
          <div
            className={clsx(
              'cp_ticket-type-image-wrapper w-12 h-12 rounded overflow-hidden flex-shrink-0',
            )}
          >
            <img
              src={image.src}
              srcSet={image.srcSet}
              className="cp_ticket-type-image object-cover h-full w-full"
            />
          </div>
        )}

        <div className="cp_ticket-type-text-wrapper gap-x-1.5 gap-y-0.5 flex flex-col">
          {showName && (
            <div className="cp_ticket-type-name overflow-hidden flex-shrink-0 font-medium">
              {ticketType.name}
            </div>
          )}
        </div>

        <div className="cp_ticket-type-price-wrapper ml-auto">
          {showPrice && (
            <div
              className={clsx('cp_ticket-type-price-container gap-x-1.5 gap-y-0.5 flex flex-col', {
                'text-right': showName && alignment === 'left',
                'flex-1': !showName,
              })}
            >
              <div
                className={clsx(
                  'cp_ticket-type-price whitespace-nowrap space-y-1 font-medium w-full',
                  {
                    'text-right': alignment === 'left',
                  },
                )}
              >
                {price}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
